/**
 * /admin/webhooks — Webhook Catalog + Test Fire (Make.com wiring helper)
 *
 * Shows every event the backend emits with the exact payload shape,
 * "Send Test" button per event, and a copy-to-clipboard JSON viewer.
 * Eliminates trial-and-error in the Make.com Event Router UI.
 */
import React, { useEffect, useState } from "react";
import {
  Loader2, Send, CheckCircle2, AlertCircle, Copy, Check, Zap,
} from "lucide-react";
import api from "../api";

export default function AdminWebhooks() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [firing, setFiring] = useState(null);
  const [results, setResults] = useState({});

  useEffect(() => {
    api
      .get("/admin/webhooks/catalog")
      .then(({ data }) => setCatalog(data))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  async function fire(eventName) {
    setFiring(eventName);
    setResults((r) => ({ ...r, [eventName]: null }));
    try {
      const { data } = await api.post("/admin/webhooks/test", { event: eventName });
      setResults((r) => ({ ...r, [eventName]: { ok: true, ...data } }));
    } catch (e) {
      setResults((r) => ({
        ...r,
        [eventName]: { ok: false, error: e.response?.data?.detail || e.message },
      }));
    } finally {
      setFiring(null);
    }
  }

  return (
    <div className="container-cd py-12" data-testid="admin-webhooks-page">
      <div className="overline">ADMIN · CORRIDOR · WEBHOOKS</div>
      <h1 className="font-display text-5xl mt-3 leading-[0.9]">Webhook catalog</h1>
      <p className="mt-4 text-sm max-w-2xl" style={{ color: "var(--ink-2)" }}>
        Every event the backend emits to <code>MAKE_RFQ_WEBHOOK_URL</code>. Copy the field
        names into your Make.com Event Router filters. Hit <strong>Send Test</strong> to
        fire a sample payload so you can map fields in the next module without waiting
        for real traffic. Test payloads include a top-level <code>_test: true</code> flag
        so you can choose to drop them in production filters.
      </p>

      {err && (
        <div className="mt-4 cd-card p-4 font-mono text-xs" style={{ color: "#dc2626" }}>
          {err}
        </div>
      )}

      {loading ? (
        <div className="overline mt-10" data-testid="webhooks-loading">LOADING…</div>
      ) : (
        <>
          <div
            className="mt-8 cd-card p-5 flex items-center gap-3"
            data-testid="webhooks-status"
            style={{
              borderColor: catalog.webhook_url_configured ? "var(--green)" : "#dc2626",
              background: catalog.webhook_url_configured ? "rgba(34,197,94,0.05)" : "rgba(220,38,38,0.05)",
            }}
          >
            {catalog.webhook_url_configured ? (
              <CheckCircle2 size={18} style={{ color: "var(--green)" }} />
            ) : (
              <AlertCircle size={18} style={{ color: "#dc2626" }} />
            )}
            <div>
              <div className="font-mono text-xs overline">
                MAKE_RFQ_WEBHOOK_URL · {catalog.webhook_url_configured ? "CONFIGURED" : "NOT SET"}
              </div>
              <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                {catalog.webhook_url_configured
                  ? "All events fire to your Make.com scenario."
                  : "Set MAKE_RFQ_WEBHOOK_URL in backend/.env to enable webhook delivery."}
              </div>
            </div>
          </div>

          {/* Make.com — drop _test events filter docs */}
          <div
            className="mt-4 cd-card p-5"
            style={{ background: "var(--ink)", color: "white", borderColor: "var(--ink)" }}
            data-testid="webhooks-test-filter-docs"
          >
            <div
              className="overline mb-2 flex items-center gap-2"
              style={{ color: "#3b82f6" }}
            >
              <AlertCircle size={11} /> RECOMMENDED · DROP TEST EVENTS IN PRODUCTION
            </div>
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.78)" }}>
              Every <strong>Send Test</strong> button on this page fires a real payload to
              your scenario with a top-level <code>_test: true</code> flag. Add this filter to
              <strong> every production branch</strong> of your Event Router so test fires (and
              backend pytest runs) never trigger real WhatsApp / email sends to plants or buyers.
            </p>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <div>
                <div
                  className="overline mb-1.5"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                >
                  STEP 1 — In Make.com Event Router
                </div>
                <p className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
                  Open each production branch (RFQ, signup, plant_view, etc.) → click the
                  filter funnel between the Router and the first Action module.
                </p>
              </div>
              <div>
                <div
                  className="overline mb-1.5"
                  style={{ color: "rgba(255,255,255,0.55)" }}
                >
                  STEP 2 — Add this filter condition
                </div>
                <pre
                  className="font-mono p-3 overflow-x-auto"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    color: "#86efac",
                    fontSize: 11,
                    lineHeight: 1.45,
                    borderRadius: 2,
                  }}
                  data-testid="webhooks-test-filter-snippet"
                >
{`Label: "Skip test events"

Condition:  {{1.\`_test\`}}
Operator:   Text operators → Does not exist
            (OR: Numeric → Not equal to → true)`}
                </pre>
              </div>
            </div>
            <p
              className="text-xs mt-3"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              For a separate test-routing branch (e.g. log to a "test events" Google
              Sheet), invert: filter <code>_test exists</code> and send those rows
              there instead.
            </p>
          </div>

          <div className="mt-8 space-y-4">
            {catalog.events.map((e) => (
              <EventCard
                key={e.event}
                spec={e}
                firing={firing === e.event}
                result={results[e.event]}
                onFire={() => fire(e.event)}
                disabled={!catalog.webhook_url_configured}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventCard({ spec, firing, result, onFire, disabled }) {
  const [copied, setCopied] = useState(false);
  const json = JSON.stringify(spec.sample_payload, null, 2);

  async function copyJson() {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }

  return (
    <div className="cd-card p-5 md:p-6" data-testid={`webhook-card-${spec.event}`}>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="cd-chip cd-chip-dark font-mono"
              style={{ fontSize: 11 }}
              data-testid={`webhook-event-name-${spec.event}`}
            >
              event = "{spec.event}"
            </span>
            <span className="font-heading font-semibold text-lg">{spec.label}</span>
          </div>
          <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
            {spec.description}
          </p>
          <div className="mt-2 font-mono text-xs" style={{ color: "var(--ink-2)" }}>
            <Zap size={11} className="inline mr-1" />
            Fired from: <code>{spec.fired_from}</code>
          </div>
        </div>
        <div className="flex md:flex-col gap-2 flex-wrap">
          <button
            onClick={onFire}
            disabled={disabled || firing}
            className="btn btn-primary"
            style={{ minWidth: 130 }}
            data-testid={`webhook-test-${spec.event}`}
          >
            {firing ? (
              <><Loader2 size={14} className="animate-spin" /> Firing…</>
            ) : (
              <><Send size={14} /> Send Test</>
            )}
          </button>
          <button
            onClick={copyJson}
            className="btn btn-ghost"
            style={{ minWidth: 130 }}
            data-testid={`webhook-copy-${spec.event}`}
          >
            {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy JSON</>}
          </button>
        </div>
      </div>

      {result && (
        <div
          className="mt-3 p-3 font-mono text-xs"
          style={{
            background: result.ok ? "rgba(34,197,94,0.06)" : "rgba(220,38,38,0.06)",
            border: `1px solid ${result.ok ? "rgba(34,197,94,0.3)" : "rgba(220,38,38,0.3)"}`,
            color: result.ok ? "var(--ink)" : "#dc2626",
          }}
          data-testid={`webhook-result-${spec.event}`}
        >
          {result.ok ? (
            <span>
              <CheckCircle2 size={11} className="inline mr-1.5 -mt-0.5" />
              Make.com responded {result.status_code}: {result.response_text || "(empty)"}
            </span>
          ) : (
            <span>
              <AlertCircle size={11} className="inline mr-1.5 -mt-0.5" />
              {result.error}
            </span>
          )}
        </div>
      )}

      <details className="mt-4" data-testid={`webhook-payload-${spec.event}`}>
        <summary
          className="overline cursor-pointer"
          style={{ userSelect: "none" }}
        >
          Sample payload — {Object.keys(spec.sample_payload).length} fields
        </summary>
        <pre
          className="mt-3 p-3 font-mono overflow-x-auto"
          style={{
            background: "var(--ink)",
            color: "#e8edf7",
            fontSize: 11,
            lineHeight: 1.5,
            borderRadius: 2,
            maxHeight: 360,
          }}
        >
          {json}
        </pre>
      </details>
    </div>
  );
}
